
import { Component, Mixins, Prop } from 'vue-property-decorator';
import { IdState } from 'vue-virtual-scroller';
import { namespace } from 'vuex-class';

import BackendTrackingMixin from '@/mixins/BackendTracking.vue';
import ImpressionTrackingMixin from '@/mixins/ImpressionTrackingMixin.vue';
import TitleWatchlistMutationMixin from '@/mixins/title-quick-action-mutations/TitleWatchlistMutationMixin.vue';

import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import ImpressionPixel from '@/components/sponsored-recommendations/ImpressionPixel.vue';
import RecommendationAdPill from '@/components/sponsored-recommendations/RecommendationAdPill.vue';
import SRTitleListGraphql from '@/components/SRTitleListGraphql.vue';
import ListToggle from '@/components/sponsored-recommendations/actions/ListToggle.vue';

import { SnowplowContext, SnowplowModuleContext } from '@/helpers/tracking/providers/snowplow-contexts';

import { ExternalTracker, ExternalTrackerType, SraEventAction, ObjectType } from '@/@types/graphql-types';
import { AssertByTypename } from '@/helpers/graphql-model-helper';
import type { SponsoredAdFragmentFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';
import type { HorizontalTitleListItem } from '@/components/timeline/HorizontalTitleListGraphql.vue';

const IdStateMixin = IdState({
	idProp: (vm: SponsoredRecommendationList) => vm.campaignNode.id ?? 'List-NOID',
});

const Language = namespace('language');
const TitleDetailsStore = namespace('titleDetails');

@Component<SponsoredRecommendationList>({
	name: 'SponsoredRecommendationList',
	components: {
		ImpressionPixel,
		ProviderIcon,
		RecommendationAdPill,
		SRTitleListGraphql,
		ListToggle,
	},
	// virtual scroller persistent state
	idState() {
		return {
			imageLoaded: false,
		};
	},
})
export default class SponsoredRecommendationList extends Mixins(
	BackendTrackingMixin,
	ImpressionTrackingMixin,
	IdStateMixin,
	TitleWatchlistMutationMixin
) {
	impressionPixelsInternal: Array<string> = [];
	impressionPixelsExternal: Array<string> = [];

	@Prop({ default: () => [] }) declare additionalContexts: (SnowplowModuleContext | SnowplowContext)[];
	@Prop({ required: true }) declare sponsoredAd: SponsoredAdFragmentFragment;
	@Prop({ default: true }) lazy: boolean;
	@Prop({ default: false }) fullWidth: boolean;
	@Prop({ default: () => [] }) supportedObjectTypes: ObjectType[];

	@Language.Getter declare language: string;

	@TitleDetailsStore.Action setSponsoredRecommendation: (sponsoredAd: SponsoredAdFragmentFragment) => void;

	/** IMPRESSION TRACKING */
	get observable() {
		return this.getObservableOptions(this.campaignNode.id);
	}
	/** IMPRESSION TRACKING */

	get campaignNode() {
		return this.sponsoredAd.campaign!.node as AssertByTypename<
			SponsoredAdFragmentFragment['campaign']['node'],
			'GenericTitleList'
		>;
	}

	get titles() {
		return this.campaignNode.titles.edges?.map(edge => edge.node);
	}

	get impressionTagInternal() {
		return '';
	}

	get impressionTagExternal() {
		return '';
	}

	get providerId() {
		return this.sponsoredAd.campaign.watchNowOffer.package.packageId;
	}

	mounted() {
		this.injectExternalImpressionHtml();
	}

	// needed by ImpressionTrackingMixin
	impressionCallback() {
		this.$backendTracking.trackSraEvent(SraEventAction.Impression, this.sponsoredAd);

		this.externalTrackingImpressionUrl();

		if (this.impressionTagInternal) this.addImpressionTrackingPixelInternal();
		if (this.impressionTagExternal) this.addImpressionTrackingPixelExternal();
	}

	get externalClickoutUrl(): ExternalTracker['data'] | undefined {
		const tracker = this.sponsoredAd.campaign.externalTrackers.find(
			(e: ExternalTracker) => e.type === ExternalTrackerType.ClickoutUrl
		);
		return tracker?.data;
	}

	get externalImpressionUrl(): ExternalTracker | undefined {
		return this.sponsoredAd.campaign.externalTrackers.find(
			(e: ExternalTracker) => e.type === ExternalTrackerType.ImpressionUrl
		);
	}

	get externalImpressionHtml(): ExternalTracker | undefined {
		return this.sponsoredAd.campaign.externalTrackers.find(
			(e: ExternalTracker) => e.type === ExternalTrackerType.ImpressionHtml
		);
	}

	get externalClickout() {
		return this.sponsoredAd.campaign.externalTrackers.find(
			(e: ExternalTracker) => e.type === ExternalTrackerType.ClickoutUrl
		);
	}

	onSRListPosterClicked({ title, index }: { title: HorizontalTitleListItem; index: number }) {
		this.setSponsoredRecommendation(this.sponsoredAd);

		this.$backendTracking.trackSraEvent(SraEventAction.Click, this.sponsoredAd, title.id, index);

		// pass down click event after title got clicked
		this.$emit('click', { title, index, titleList: this.campaignNode.id });
	}

	onSRListClick() {
		this.$backendTracking.trackSraEvent(SraEventAction.Click, this.sponsoredAd);
		this.$backendTracking.trackSraEvent(SraEventAction.ListClickout, this.sponsoredAd);
	}

	onFollowClicked() {
		this.$backendTracking.trackSraEvent(SraEventAction.FollowClicked, this.sponsoredAd);
	}

	private externalTrackingImpressionUrl() {
		if (this.externalImpressionUrl) {
			const url = this.externalImpressionUrl.data;
			try {
				fetch(url, {
					mode: 'no-cors',
				});
			} catch (err: any) {}
		}
	}

	private injectExternalImpressionHtml() {
		if (this.externalImpressionHtml?.data) {
			const fragment = document.createRange().createContextualFragment(this.externalImpressionHtml?.data);
			document.head.appendChild(fragment);
		}
	}

	private addImpressionTrackingPixelInternal() {
		this.impressionPixelsInternal.push('_');
	}

	private addImpressionTrackingPixelExternal() {
		this.impressionPixelsExternal.push('_');
	}
}
